import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { Notification } from '@/types/database';

/* ─────────────────────────────────────────────────────────────────────────────
   useNotifications  (Phase 63 — Universal In-App Notifications)

   Reads from the new `notifications` table (Phase 63).
   Backward-compatible: hr_notifications is untouched.

   Features:
   • Fetches the 30 most-recent rows for the user (read + unread).
   • Realtime subscription: INSERT/UPDATE on notifications for this user_id
     is reflected instantly — badge count stays live without polling.
   • markOneRead(id)   — marks a single row is_read=true, updates local state.
   • markAllRead()     — bulk-updates all unread rows, clears badge.
   • unreadCount       — derived from local state (no extra query).
───────────────────────────────────────────────────────────────────────────── */

export interface UseNotificationsResult {
  /** Recent notifications — both read and unread, newest first */
  notifications:  Notification[];
  /** Number of unread items — drives the bell badge */
  unreadCount:    number;
  /** True while the initial fetch is in flight */
  loading:        boolean;
  /** Mark a single notification as read in the DB + local state */
  markOneRead:    (id: string) => Promise<void>;
  /** Mark every unread notification as read */
  markAllRead:    () => Promise<void>;
  /** Force re-fetch from the DB */
  refresh:        () => Promise<void>;
}

const MAX_FETCH = 30; // how many rows to show in the popover

export function useNotifications(userId: string | undefined): UseNotificationsResult {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading,       setLoading]       = useState(false);

  // Stable ref so realtime callbacks never close over a stale userId
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  // ── Fetch ────────────────────────────────────────────────────────────────

  const fetchNotifications = useCallback(async () => {
    const uid = userIdRef.current;
    if (!uid) { setNotifications([]); return; }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('id, user_id, title, message, action_url, is_read, created_at')
        .eq('user_id', uid)
        .order('created_at', { ascending: false })
        .limit(MAX_FETCH);

      if (!error && data) {
        setNotifications(data as Notification[]);
      } else if (error) {
        console.error('[useNotifications] fetch error:', error.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Mark one read ────────────────────────────────────────────────────────

  const markOneRead = useCallback(async (id: string) => {
    // Optimistic update first
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
    );
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', id);

    if (error) {
      console.error('[useNotifications] markOneRead error:', error.message);
      // Roll back on failure
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: false } : n)),
      );
    }
  }, []);

  // ── Mark all read ────────────────────────────────────────────────────────

  const markAllRead = useCallback(async () => {
    const uid = userIdRef.current;
    if (!uid) return;

    const unreadIds = notifications.filter((n) => !n.is_read).map((n) => n.id);
    if (unreadIds.length === 0) return;

    // Optimistic update
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));

    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', uid)
      .eq('is_read', false);

    if (error) {
      console.error('[useNotifications] markAllRead error:', error.message);
      // Roll back
      setNotifications((prev) =>
        prev.map((n) =>
          unreadIds.includes(n.id) ? { ...n, is_read: false } : n,
        ),
      );
    }
  }, [notifications]);

  // ── Initial load + re-fetch on userId change ─────────────────────────────

  useEffect(() => {
    fetchNotifications();
  }, [userId, fetchNotifications]);

  // ── Realtime subscription ────────────────────────────────────────────────
  //
  // Listens for INSERT (new notification) and UPDATE (is_read changed from
  // another session) events on the notifications table for this user.
  // This keeps the badge count accurate across tabs and devices.

  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`notifications:user_id=eq.${userId}`)
      .on(
        'postgres_changes',
        {
          event:  'INSERT',
          schema: 'public',
          table:  'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const row = payload.new as Notification;
          // Prepend to list so it appears at the top; respect MAX_FETCH cap
          setNotifications((prev) => [row, ...prev].slice(0, MAX_FETCH));
        },
      )
      .on(
        'postgres_changes',
        {
          event:  'UPDATE',
          schema: 'public',
          table:  'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const updated = payload.new as Notification;
          setNotifications((prev) =>
            prev.map((n) => (n.id === updated.id ? { ...n, ...updated } : n)),
          );
        },
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [userId]);

  // ── Derived state ────────────────────────────────────────────────────────

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return {
    notifications,
    unreadCount,
    loading,
    markOneRead,
    markAllRead,
    refresh: fetchNotifications,
  };
}
